import React from 'react';
import _assign from 'lodash/assign';
import _camelCase from 'lodash/camelCase';
import _defaultTo from 'lodash/defaultTo';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _indexOf from 'lodash/indexOf';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _join from 'lodash/join';
import _orderBy from 'lodash/orderBy';
import _reduce from 'lodash/reduce';
import _words from 'lodash/words';
// Serialization
import { deserialize, serialize } from 'serializr';
import { simcardSchema, simcardGroupSchema } from './schemas';
// Localization
import { LocalizedMessage } from '../intl/LocalizedComponents';
// Color
import { simcardStatusPalette } from '../components/core/colors';
// Permissions
import permissions from '../constants/Permissions';
// Custom
import { SEARCHFIELD_TYPES } from '../constants/App';
import { findInList } from '../utils/Tools';

/**
* Serialization
*/
const serializerKey = 'simcard';

const deserializeSimcard = (data) => deserialize(simcardSchema, data);

const serializeSimcard = ({ iccid, number, groupId }) => ({
  [serializerKey]: {
    ...serialize(simcardSchema, {
      iccid: _defaultTo(iccid, null),
      number: _defaultTo(number, null),
      group_id: _defaultTo(groupId, null),
    }),
  },
});
const serializeMultiSimcards = ({ simcards, groupId }) => ({
  ...(simcards && { simcards }),
  ...(groupId && { simcard_group_id: groupId }),
});

const deserializeSimcardGroup = (data) => deserialize(simcardGroupSchema, data);

const serializeSimcardGroup = (name) => ({
  simcard_group: {
    ...serialize(simcardGroupSchema, { name }),
  },
});

/**
* Lists
*/
const simcardStatusList = [
  {
    key: 'unknown',
    value: 'unknown',
    labelId: 'simcard.status.unknown',
    icon: 'simcardStatusUnknown',
    color: simcardStatusPalette.unknown,
  },
  {
    key: 'activationInProgress',
    value: 'activation_in_progress',
    labelId: 'simcard.status.activationInProgress',
    icon: 'simcardStatusActivationInProgress',
    color: simcardStatusPalette.activation_in_progress,
  },
  {
    key: 'active',
    value: 'active',
    labelId: 'simcard.status.active',
    icon: 'simcardStatusActive',
    color: simcardStatusPalette.active,
  },
  {
    key: 'inactive',
    value: 'inactive',
    labelId: 'simcard.status.inactive',
    icon: 'simcardStatusInactive',
    color: simcardStatusPalette.inactive,
  },
  {
    key: 'suspended',
    value: 'suspended',
    labelId: 'simcard.status.suspended',
    icon: 'simcardStatusSuspended',
    color: simcardStatusPalette.suspended,
  },
];

/**
* Utils
*/
const sortSimcardGroups = (simcardGroups, fields = ['name'], dirs = ['asc']) => (_orderBy(simcardGroups, fields, dirs));

const simcardTitle = (simcardData) => _get(simcardData, 'iccid', '');

const simcardStatus = (simcard) => findInList(simcardStatusList, 'value', simcard, 'status');

const simcardImportLineIsValid = (line) => (_has(line, 'iccid') && _has(line, 'number'));

const simcardImportLineIsDuplicate = (line) => {
  if (!simcardImportLineIsValid(line)) {
    return false;
  }
  return _isArray(line.iccid) && _camelCase(line.iccid[0]) === 'hasAlreadyBeenTaken';
};

const simcardImportFieldResult = (field, value) => (
  _isArray(value)
    ? { success: false, message: <LocalizedMessage id={`formError.${field}.${_camelCase(value[0])}`} /> }
    : { success: true, message: value }
);

const simcardImportLineSuccess = (line) => (
  simcardImportFieldResult('iccid', line.iccid).success && simcardImportFieldResult('number', line.number).success
);

const simcardImportResultInfo = (lines) => (
  _reduce(lines, (acc, line) => {
    const success = simcardImportLineSuccess(line);
    if (success) {
      _assign(acc, { valid: acc.valid + 1 });
    } else {
      _assign(acc, { invalid: acc.invalid + 1 });
    }
    return acc;
  }, { valid: 0, invalid: 0 })
);

const simcardImportValidLines = (lines) => (
  _reduce(lines, (acc, line) => {
    const success = simcardImportLineSuccess(line);
    if (success) {
      acc.push(line);
    }
    return acc;
  }, [])
);

const simcardImportErrorsLines = (errors) => (
  _join(
    _reduce(errors, (acc, error) => {
      // Error is like "Row has length 1 - expected length 2 on line 9"
      const words = _words(error);
      const lineWordIndex = _indexOf(words, 'line');
      if (lineWordIndex >= 0 && lineWordIndex < words.length - 1) {
        acc.push(words[lineWordIndex + 1]);
      }
      return acc;
    }, []),
    ', ',
  )
);

/**
* Model
*/
const simcard = {
  // Name and icon
  name: 'simcard',
  icon: 'simcard',

  // Serialization
  deserialize: deserializeSimcard,
  serialize: serializeSimcard,
  serializeMulti: serializeMultiSimcards,
  deserializeGroup: deserializeSimcardGroup,
  serializeGroup: serializeSimcardGroup,

  // Lists
  statusList: simcardStatusList,

  // Utils
  title: simcardTitle,
  status: simcardStatus,
  sortGroups: sortSimcardGroups,
  importLineIsValid: simcardImportLineIsValid,
  importLineIsDuplicate: simcardImportLineIsDuplicate,
  importFieldResult: simcardImportFieldResult,
  importLineSuccess: simcardImportLineSuccess,
  importResultInfo: simcardImportResultInfo,
  importValidLines: simcardImportValidLines,
  importErrorsLines: simcardImportErrorsLines,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'iccid', dir: 'asc' },
      ],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'iccid',
      operator: SEARCHFIELD_TYPES.string.defaultOperator(),
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 4,
      md: 4,
      lg: 5,
      xl: 5,
    }),
    columns: [
      {
        key: 'iccid',
        labelId: 'field.iccid',
        field: 'iccid',
        sortable: true,
      },
      {
        key: 'number',
        labelId: 'field.number',
        field: 'number',
        sortable: true,
        className: 'hidden-xs-down',
      },
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        sortable: false,
        className: 'table-content-1',
      },
      {
        key: 'group',
        labelId: 'field.group',
        field: 'group.name',
        sortable: true,
        secondarySorts: [
          { field: 'iccid', dir: 'desc' },
        ],
        className: 'hidden-md-down',
      },
      {
        key: 'device',
        labelId: 'field.device',
        field: 'device_simcard_maps.device.serial',
        sortable: true,
        className: 'table-content-2 hidden-xs-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'iccid',
        labelId: 'field.iccid',
        field: 'iccid',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'iccid',
        },
      },
      {
        key: 'number',
        labelId: 'field.number',
        field: 'number',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        scope: 'like',
        filter: {
          field: 'number',
        },
      },
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'status',
        },
      },
      {
        key: 'groupId',
        labelId: 'field.group',
        field: 'groupId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'group_id',
        },
      },
      {
        key: 'available',
        labelId: 'field.deviceAttached',
        field: 'available',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        excludeFilterFields: ['available', 'device'],
        type: SEARCHFIELD_TYPES.boolean,
        filter: {
          field: 'available',
        },
      },
      {
        key: 'device',
        permissions: {
          needed: permissions.pDevice.read,
          permissionsOrContext: 'userPermissions',
        },
        labelId: 'field.device',
        field: 'device',
        alias: 'device_id',
        initial: null,
        isUndefined: (value) => _isNil(value),
        type: SEARCHFIELD_TYPES.resource,
        needGetOnInit: true,
        filter: {
          field: 'device_simcard_maps.device_id',
          key: 'id',
        },
      },
    ],
  },

  // History columns
  historyColumns: [
    {
      key: 'iccid',
      labelId: 'field.iccid',
      field: 'iccid',
      sortable: false,
    },
    {
      key: 'begin',
      labelId: 'field.beginDate',
      field: 'begin',
      sortable: false,
    },
    {
      key: 'end',
      labelId: 'field.endDate',
      field: 'end',
      sortable: false,
    },
  ],
};

module.exports = simcard;



// WEBPACK FOOTER //
// ./models/simcard.js